import { useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import { useState } from 'react';
import { Button, Dialog, Field, Select, useToast } from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import { usePayers } from '@/features/patients/api';
import type { PatientDetail } from '@/features/patients/types';
import { useQueueActions, useServices } from '@/features/queue/api';
import { isApiError } from '@/lib/api';
import { EkycPhoto } from '@/pages/queue/EkycPhoto';
import styles from './patients.module.css';

/** "Panel if covered, else Self": the API's own default when payerId is left out. */
const DEFAULT_PAYER = 'default';
const SELF_PAYER = 'self';

/**
 * RL-2170: a returning patient found by search joins today's queue from the
 * record, without retyping the IC on the Queue page. Same call as the Queue
 * page walk-in (POST /queue): the API owns the branch, the one-ticket-a-day
 * rule (409 PATIENT_ALREADY_IN_QUEUE) and the default payer. Registering a
 * patient never queues them by itself; this is always a deliberate click.
 *
 * Same identity gate as the Queue page's walk-in: the patient's eKYC must be
 * `verified` AND the staff member ticks "Face checked against the eKYC photo".
 * Anything else (unverified, or a status this build does not know) blocks, so
 * this never skips a check the Queue page makes. The API itself does not check
 * identity on POST /queue; whether it should is a clinic decision.
 */
export function AddToQueue({ patient }: { patient: PatientDetail }) {
  const { hasPermission } = useSession();
  const { selected: branch } = useBranch();
  const { addWalkIn } = useQueueActions();
  const { data: services } = useServices();
  const { data: payers } = usePayers();
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState('');
  const [payer, setPayer] = useState(DEFAULT_PAYER);
  const [faceChecked, setFaceChecked] = useState(false);

  if (!hasPermission('queue:manage') || patient.status !== 'active') {
    return null;
  }

  const queuedHere =
    patient.inQueue && patient.inQueue.branch.id === branch?.id;
  if (queuedHere) {
    return (
      <p className={styles.infoNote}>
        Already in the queue at {patient.inQueue!.branch.name}: number{' '}
        {patient.inQueue!.number}.
      </p>
    );
  }
  if (!branch) {
    return (
      <p className={styles.warnNote}>
        Choose a branch in the branch switch to add this patient to the queue.
      </p>
    );
  }

  // One active service needs no choice; otherwise the clinic picks (as on Queue).
  const service = chosen || (services?.length === 1 ? services[0].id : '');
  // Exactly the Queue page's rule: only `verified` passes.
  const verified = patient.ekyc.status === 'verified';

  function close() {
    setOpen(false);
    setChosen('');
    setPayer(DEFAULT_PAYER);
    setFaceChecked(false);
  }

  async function add() {
    if (!verified || !faceChecked) return; // the button is disabled; belt and braces
    try {
      const ticket = await addWalkIn.mutateAsync({
        patientId: patient.id,
        serviceId: service,
        // Omitted = the patient's live panel cover, else Self (API default).
        ...(payer === DEFAULT_PAYER
          ? {}
          : { payerId: payer === SELF_PAYER ? null : payer }),
      });
      await qc.invalidateQueries({ queryKey: ['patient', patient.id] });
      void qc.invalidateQueries({ queryKey: ['patients'] });
      toast.show({
        tone: 'success',
        title: `Ticket ${ticket.number} · ${ticket.patient.fullName}`,
        body: `Added to the ${branch!.name} queue.`,
      });
      close();
    } catch (err) {
      toast.show({
        tone: 'error',
        title: isApiError(err)
          ? (err.problem?.title ?? 'Not done')
          : 'Not done',
        body: isApiError(err) ? err.message : undefined,
        error: err,
      });
      // Someone else queued them meanwhile: show the ticket the record now has.
      if (isApiError(err) && err.code === 'PATIENT_ALREADY_IN_QUEUE') {
        void qc.invalidateQueries({ queryKey: ['patient', patient.id] });
        close();
      }
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="primary"
        icon={<UserPlus size={16} />}
        onClick={() => setOpen(true)}
      >
        Add to queue
      </Button>
      <Dialog
        open={open}
        onClose={close}
        variant="modal"
        title={`Add ${patient.fullName} to the queue`}
        footer={
          <>
            <Button type="button" variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              icon={<UserPlus size={16} />}
              disabled={!service || !verified || !faceChecked}
              loading={addWalkIn.isPending}
              onClick={() => void add()}
            >
              Add to queue
            </Button>
          </>
        }
      >
        <p className={styles.infoNote}>
          Walk-in ticket for today at {branch.name}.
        </p>
        {verified ? (
          <>
            <EkycPhoto
              name={patient.fullName}
              verifiedAt={patient.ekyc.verifiedAt}
            />
            <label className={styles.flagLine}>
              <input
                type="checkbox"
                checked={faceChecked}
                onChange={(e) => setFaceChecked(e.target.checked)}
              />
              Face checked against the eKYC photo
            </label>
          </>
        ) : (
          <p className={styles.warnNote} role="alert">
            This patient’s identity is not verified (eKYC), so they cannot be
            added to the queue from here. Verify eKYC at the counter first, then
            try again. The Queue page asks for the same check.
          </p>
        )}
        {services?.length === 0 && (
          <p className={styles.warnNote} role="alert">
            No active service is set up, so a ticket cannot be created yet. Ask
            a clinic manager to add one under Setup → Services, then try again.
          </p>
        )}
        <Field label="Service">
          <Select
            value={service}
            onChange={(e) => setChosen(e.target.value)}
            placeholder="Choose a service"
            options={(services ?? []).map((s) => ({
              value: s.id,
              label: s.name,
            }))}
          />
        </Field>
        <Field label="Who pays">
          <Select
            value={payer}
            onChange={(e) => setPayer(e.target.value)}
            options={[
              {
                value: DEFAULT_PAYER,
                label: 'Patient’s panel if covered, else Self',
              },
              { value: SELF_PAYER, label: 'Self' },
              ...(payers ?? []).map((x) => ({ value: x.id, label: x.name })),
            ]}
          />
        </Field>
      </Dialog>
    </>
  );
}
